import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, Button, Form, Input} from 'antd';
import {Collapse} from 'react-collapse';
import AntdModal from '../../../AntdModal';
import {formHelpers} from '../../../../utils/helpers';
import {validation} from '../../../../utils/validation';


const {Item} = Form;

const defaultFormValues = {
  old_password: '',
  first_password: '',
  second_password: ''
};

const ChangePasswordModal = ({
  open,
  handleOk,
  handleCancel,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      form.setFieldsValue(defaultFormValues);
      error && setError(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const validationRules = useMemo(() => ({
    password: validation.password(t, 6),
    passwordConfirm: validation.passwordConfirm(t, 6, 'first_password'),
  }), [t]);

  const handleSubmit = (fields) => {
    if (handleOk) {
      setLoading(true);
      handleOk(
        fields,
        () => setLoading(false),
        (error) => {
          if (error.message) {
            setError(error.message);
          } else {
            const fields = formHelpers.getFormServerErrorFields(error);
            form.setFields(fields);
          }
          setLoading(false);
        }
      )
    }
  }

  const onCancel = () => handleCancel && handleCancel();

  const inputProps = {
    maxLength: 6,
    size: 'large',
    type: 'number'
  }

  const password = t('password');
  const title = `${t('change')} ${password}`;

  const footer = [
    <Button
      key='cancel'
      disabled={loading}
      onClick={onCancel}
      size='large'
    >
      {t('cancel')}
    </Button>,
    <Button
      key='submit'
      disabled={loading}
      onClick={() => form.submit()}
      size='large'
      type='primary'
    >
      {t('save')}
    </Button>
  ];


  return (
    <AntdModal
      forceRender
      footer={footer}
      onCancel={onCancel}
      open={open}
      title={title}
      data-testid='change-password-modal'
      {...rest}
    >
      <Form
        initialValues={defaultFormValues}
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
      >
        <Item
          label={`${t('current')} ${password}`}
          name='old_password'
          rules={validationRules.password}
          required
        >
          <Input.Password {...inputProps} />
        </Item>
        <Item
          label={`${t('new')} ${password}`}
          name='first_password'
          rules={validationRules.password}
          required
        >
          <Input.Password {...inputProps} />
        </Item>
        <Item
          label={`${t('confirm')} ${password}`}
          name='second_password'
          rules={validationRules.passwordConfirm}
          required
        >
          <Input.Password {...inputProps} />
        </Item>

        <Collapse isOpened={error}>
          <Alert message={error} type='error' showIcon />
        </Collapse>
      </Form>

    </AntdModal>
  );
}

ChangePasswordModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleOk: PropTypes.func.isRequired,
}

export default ChangePasswordModal;
